import { useExchangeApi } from '@/exchanges/hooks/useExchangeApi'
import { TTableAccessor } from '@/ui/molecules/Table/Table'
import { useActiveExchage } from '@/utils/hooks/useActiveExchange'
import { useEffect, useMemo, useState } from 'react'
import style from '../style.module.scss'
import { isExchangeBinance, isExchangeBybit, isExchangeTigerX } from '@/utils/lib/exchange'
import { getBinanceHistoryKeysTranslations } from '../HistoryTrade.translations/binanceHistoryDictionary'
import { getBybitHistoryKeysTranslations } from '../HistoryTrade.translations/bybitHistoryDictionaries'
import {
  HISTORY_BINANCE_COLUMNS_TO_SHOW,
  HISTORY_BYBIT_COLUMNS_TO_SHOW,
  HISTORY_TIGERX_COLUMNS_TO_SHOW,
} from '@/core/config/history'
import { getTigerXHistoryKeysTranslations } from '../HistoryTrade.translations/tigerXHistoryDictionary'
import { getInitialFilters } from './initialsFilters'

export const useHistoryTradeData = () => {
  const exchangeApi = useExchangeApi()
  const { activeExchange } = useActiveExchage()
  const initialFilters = useMemo(() => getInitialFilters(activeExchange.type), [activeExchange.type])
  const [filters, updateFilters] = useState<any>(initialFilters)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<number | null>(null)
  const [historyTradeData, setHistoryTradeData] = useState<any | null>(null)
  const [reachedEnd, setReachedEnd] = useState(false)
  const [page, setPage] = useState<number>(1)
  const [cursor, setCursor] = useState<string | null>(null)

  const fetchHistoryTradeData = async (filters: any, isPaginationLoad = false) => {
    setLoading(true)
    setError(null)
    setErrorCode(null)

    try {
      const data = await exchangeApi.getHistoryTradeData(filters)

      if (!data) {
        throw new Error('No withdraw data has been received')
      }

      if (data.error) {
        setErrorCode(data.error.status) // Сохраняем код ошибки
        throw new Error(`${data.error.source}: ${data.error.message} (code: ${data.error.status})`)
      }

      setPage(data.page ?? 1)
      setReachedEnd(data.reachedEnd)

      if (data?.nextCursor !== undefined) {
        setCursor(data.nextCursor)
      }

      if (isPaginationLoad) {
        setHistoryTradeData([...historyTradeData, ...data.historyFormatted])
      } else {
        setHistoryTradeData(data.historyFormatted)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHistoryTradeData(filters, filters.page > 1)
  }, [filters])

  const loadMore = () => {
    if (reachedEnd || loading) return

    const isBybit = isExchangeBybit(activeExchange.type)

    if (isBybit) {
      fetchHistoryTradeData({ ...filters, cursor }, true)
    } else {
      const nextPage = page + 1
      const nextFilters = { ...filters, page: nextPage }
      updateFilters(nextFilters)
    }
  }

  const tableColumns: TTableAccessor[] = useMemo(() => {
    let translations: Record<string, string> = {}
    let columnsToShow: string[] = []
    let nowrapColumn = ''

    switch (true) {
      case isExchangeBinance(activeExchange.type):
        translations = getBinanceHistoryKeysTranslations()
        columnsToShow = HISTORY_BINANCE_COLUMNS_TO_SHOW
        nowrapColumn = 'time'
        break

      case isExchangeBybit(activeExchange.type):
        translations = getBybitHistoryKeysTranslations()
        columnsToShow = HISTORY_BYBIT_COLUMNS_TO_SHOW
        nowrapColumn = 'transactionTime'
        break

      case isExchangeTigerX(activeExchange.type):
        translations = getTigerXHistoryKeysTranslations()
        columnsToShow = HISTORY_TIGERX_COLUMNS_TO_SHOW
        nowrapColumn = 'createAt'
        break

      default:
        return []
    }

    return columnsToShow.map(columnId => ({
      accessor: columnId,
      header: <>{translations[columnId]}</>,
      meta: {
        className: columnId === nowrapColumn ? [style.nowrap] : [],
      },
    }))
  }, [activeExchange.type])

  return {
    historyTradeData,
    tableColumns,
    setFilters: (newFilters: any) => {
      setPage(1) // сброс страницы
      setReachedEnd(false)
      setHistoryTradeData([]) // очистка данных при новом фильтре
      updateFilters(newFilters)
    },
    reachedEnd,
    initialFilters,
    loadMore,
    loading,
    error,
  }
}
